// Discover screen — the swipe deck and the Browse view. The story rings for
// matched personas now live on the Chats screen instead; the match modal is
// still rendered as a portal-ish overlay from here.
//
// The Swipe vs Browse view is owned by App.tsx (so the AppHeader stays clean
// across tabs). This screen receives `view` + `onViewChange` and renders the
// pill toggle just below the AppHeader.

import { useEffect, useMemo, useState } from 'react';
import { Flame, RotateCcw } from 'lucide-react';
import type { Persona, Relationship } from '@/lib/types';
import { PERSONAS } from '@/data/personas';
import { PHOTO_PERSONAS } from '@/components/portraits';
import { SwipeCard, type SwipeDirection } from './SwipeCard';
import { MatchModal } from './MatchModal';
import { BrowseView, EMPTY_BROWSE_FILTERS, type BrowseFilterState } from './BrowseView';
import { ScreenBackdrop } from '@/components/visual/ScreenBackdrop';
import { DiscoverViewToggle, type DiscoverView } from '@/components/ui/DiscoverViewToggle';
import { C } from '@/styles/tokens';

interface DiscoverScreenProps {
  view: DiscoverView;
  onViewChange: (next: DiscoverView) => void;
  relationships: Record<string, Relationship>;
  streak: number;
  masteredCount: number;
  /** User-built personas. Joined with curated PERSONAS for Browse. */
  customPersonas: readonly Persona[];
  onMatch: (persona: Persona) => void;
  onSkip: (persona: Persona) => void;
  onSwipeAgain: () => void;
  onOpenChat: (personaId: string) => void;
  onOpenProfile: (persona: Persona) => void;
}

function shuffle<T>(items: readonly T[]): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

export function DiscoverScreen({
  view,
  onViewChange,
  relationships,
  streak,
  masteredCount,
  customPersonas,
  onMatch,
  onSkip,
  onSwipeAgain,
  onOpenChat: _onOpenChat,
  onOpenProfile,
}: DiscoverScreenProps) {
  // `seed` re-shuffles the deck order. Bumped on mount and on "Swipe again".
  const [seed, setSeed] = useState(() => Math.random());
  // Filter state lives here (not inside BrowseView) so it survives Swipe ↔
  // Browse toggles within a session — users who tweak filters, glance at the
  // deck, then come back shouldn't lose their setup.
  const [browseFilters, setBrowseFilters] = useState<BrowseFilterState>(EMPTY_BROWSE_FILTERS);
  // Swipe deck is curated-only. User-built personas don't show up here —
  // they were created intentionally, no need to re-discover via swipe.
  const shuffled = useMemo(() => {
    void seed;
    return shuffle(PERSONAS);
  }, [seed]);
  const deck = useMemo(
    () => shuffled.filter((p) => {
      const rel = relationships[p.id];
      return !rel?.matched && !rel?.passed;
    }),
    [shuffled, relationships],
  );
  const [matchModal, setMatchModal] = useState<Persona | null>(null);

  const passedCount = useMemo(
    () => PERSONAS.filter((p) => relationships[p.id]?.passed).length,
    [relationships],
  );

  // Browse pool: every persona, curated and custom. Custom personas surface
  // here so users can re-find them by HSK level / topic alongside the rest.
  const browsePool = useMemo(
    () => [...PERSONAS, ...customPersonas] as readonly Persona[],
    [customPersonas],
  );

  const top3 = deck.slice(0, 3);

  // Warm the browser cache for the next few cards in the deck. The deck only
  // renders 3 SwipeCards at a time, so without this the user sees a 1-2 second
  // lag whenever a swipe reveals card #4 (its image only starts loading then).
  // `new Image().src` triggers the fetch with no DOM impact; the service
  // worker precache will short-circuit it on repeat visits.
  useEffect(() => {
    for (const p of deck.slice(0, 6)) {
      if (PHOTO_PERSONAS.has(p.id)) {
        const img = new Image();
        img.src = `/portraits/${p.id}.jpg`;
      }
    }
  }, [deck]);

  function handleSwipe(persona: Persona, dir: SwipeDirection) {
    if (dir === 'right') {
      onMatch(persona);
      setMatchModal(persona);
    } else {
      onSkip(persona);
    }
  }

  function dismissModal() {
    setMatchModal(null);
  }

  return (
    <section className="relative h-full flex flex-col overflow-hidden">
      <ScreenBackdrop mark="缘" position="tr" size={240} rotate={-12} />

      <div className="px-4 pt-1 flex justify-center">
        <DiscoverViewToggle view={view} onChange={onViewChange} />
      </div>

      <div className="flex items-center justify-between px-5 pt-3 py-1 ff-body text-[11px]" style={{ color: C.inkLight }}>
        <div className="flex items-center gap-1.5">
          <Flame size={14} style={{ color: C.vermillion }} aria-hidden />
          {streak}-day streak
          <span className="ff-zh-serif opacity-70">· 连续 {streak} 天</span>
        </div>
        <div>
          {masteredCount} words mastered
          <span className="ff-zh-serif opacity-70 ml-1">· 已学 {masteredCount} 词</span>
        </div>
      </div>

      {view === 'browse' ? (
        <div className="flex-1 overflow-hidden">
          <BrowseView
            personas={browsePool}
            filters={browseFilters}
            onFiltersChange={setBrowseFilters}
            onOpenProfile={onOpenProfile}
          />
        </div>
      ) : (
        <div className="relative flex-1 px-4 pb-4 pt-2">
          {top3.length === 0 ? (
            <div
              className="absolute inset-0 m-4 rounded-[28px] flex flex-col items-center justify-center text-center px-8 gap-4"
              style={{ background: C.paper, color: C.inkLight }}
            >
              <div>
                <div className="ff-display text-2xl mb-1" style={{ color: C.ink }}>
                  No more cards
                </div>
                <div className="ff-zh-serif text-sm mb-2" style={{ color: C.inkLight }}>
                  没有更多了
                </div>
                <p className="ff-body text-sm">
                  You&apos;ve seen everyone for now. Tap a story above to keep practising with a match.
                </p>
              </div>
              {passedCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    onSwipeAgain();
                    setSeed(Math.random());
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full ff-body text-sm font-semibold text-white"
                  style={{ background: C.vermillion }}
                >
                  <RotateCcw size={14} aria-hidden />
                  Swipe again on {passedCount} you skipped
                </button>
              )}
            </div>
          ) : (
            [...top3].reverse().map((persona, i) => {
              const isTop = i === top3.length - 1;
              return (
                <SwipeCard
                  key={persona.id}
                  persona={persona}
                  isTop={isTop}
                  stackOffset={(top3.length - 1 - i) * 8}
                  onSwipe={(dir) => handleSwipe(persona, dir)}
                />
              );
            })
          )}
        </div>
      )}

      {matchModal && (
        <MatchModal
          persona={matchModal}
          onChat={() => {
            const id = matchModal.id;
            setMatchModal(null);
            // Defer so modal teardown doesn't fight the screen change.
            window.setTimeout(() => _onOpenChat(id), 0);
          }}
          onContinue={dismissModal}
        />
      )}
    </section>
  );
}
